import React, { Fragment, useEffect, useState, Suspense, useMemo } from 'react';
import {
  Text,
  Spinner,
  SpinnerSize,
  Box,
  Button,
  ButtonSize,
  Icon,
  Heading,
  ToastType,
  ToastAction,
  ButtonAppearance,
  useToastsDispatch,
} from '@bechtel/gds';
import { get, isEqual, isNull, map } from 'lodash';
import {
  FooterButtonStyled,
  FooterStyled,
  ProjectAdminContainerStyled,
  ProjectAdminHeaderStyled,
  ProjectAdminSubContainerStyled,
} from '~/styled';
import { t, Trans } from '@lingui/macro';
import { GET_PROJECT_CONFIG_FEATURES, SAVE_PROJECT_FEATURES } from '@bechtel/client-gql';
import { useQuery, useMutation, ApolloError } from '@apollo/client';
import { useAppUser, ErrorBoundary, SuspenseLoader } from '@bechtel/client-common';
import { Formik, FormikProps } from 'formik';
import { FormikState } from './types';
import { FeatureItem } from './item';
import { Feature, SaveFeaturesError, SaveFeatureInput, SaveFeaturesPayload } from '@bechtel/types';
import SaveFeatureErrorModal from './error';
import { array, object, number, string, lazy as yupLazy } from 'yup';
const formikInitialValues: FormikState = {
  features: [],
};
export const Features = () => {
  const [initialValues, setInitialValues] = useState<FormikState>(formikInitialValues);
  const { user } = useAppUser();
  const [saveFeatureError, setSaveFeatureError] = useState<SaveFeaturesError | ApolloError | null>(null);
  const dispatchToast = useToastsDispatch();
  const {
    loading: featuresLoading,
    error: featuresError,
    data: featuresData,
    refetch: featuresRefetch,
  } = useQuery(GET_PROJECT_CONFIG_FEATURES, {
    notifyOnNetworkStatusChange: true,
    variables: {
      id: user?.project?.id,
    },
  });

  const featuresList = useMemo(() => {
    return get(featuresData, 'projectConfigById.features', []);
  }, [featuresData]);

  useEffect(() => {
    setInitialValues({ features: featuresList });
  }, [featuresList]);

  const SaveFeatureToast = () => {
    return (
      <Box p={{ sm: '500' }} flexDirection={{ sm: 'column' }}>
        <Text family="graphik" weight="600" size="500">
          <Trans>Feature data saved successfully.</Trans>
        </Text>
      </Box>
    );
  };

  const onSaveFeatureComplete = (data: { saveFeatures: SaveFeaturesPayload }) => {
    const saveFeatureError = get(data, 'saveFeatures.error', undefined);
    if (saveFeatureError) {
      setSaveFeatureError(saveFeatureError);
    } else {
      const toastAction: ToastAction = {
        type: 'ADD_TOAST',
        payload: {
          toastType: ToastType.INFO,
          content: SaveFeatureToast,
          autoDismissTimeout: 5000,
        },
      };
      dispatchToast(toastAction);
    }
  };

  const [updateFeatures, { loading: saveFeatureLoading }] = useMutation<
    { saveFeatures: SaveFeaturesPayload },
    { input: SaveFeatureInput[] }
  >(SAVE_PROJECT_FEATURES, {
    onCompleted: onSaveFeatureComplete,
    onError: (error) => {
      setSaveFeatureError(error);
    },
  });
  const saveFeatures = (featuresItems: Feature[]) => {
    updateFeatures({
      variables: {
        input: featuresItems.map(({ id, enabled, metaData }) => ({
          featureId: id,
          enabled: enabled,
          metaData: JSON.stringify(metaData),
        })),
      },
    });
  };

  const FeatureValidationSchema = object().shape({
    features: array().of(
      object().shape({
        metaData: object()
          .shape({
            properties: yupLazy((values) => {
              const shapes = {};
              const FIELD_KEYS = Object.keys(values);
              FIELD_KEYS.forEach((fieldKey) => {
                if (values[fieldKey]?.type?.toLowerCase() == 'number') {
                  shapes[fieldKey] = object().shape({
                    value: number()
                      .when('min', (min, yup) => min && yup.min(min, t`Min value ${min}.`))
                      .when('max', (max, yup) => max && yup.max(max, t`Max value ${max}.`))
                      .typeError(t`Only number.`)
                      .nullable()
                      .notRequired()
                      .when('required', {
                        is: (required) => required && required === true,
                        then: number()
                          .required(t`Is required.`)
                          .nullable(),
                      }),
                  });
                }
                if (values[fieldKey]?.type?.toLowerCase() == 'text') {
                  shapes[fieldKey] = object().shape({
                    value: string()
                      .when('min', (min, yup) => min && yup.min(min, t`Too short.`))
                      .when('max', (max, yup) => max && yup.max(max, t`Too long.`))
                      .nullable()
                      .notRequired()
                      .when('required', {
                        is: (required) => required && required === true,
                        then: string()
                          .required(t`Is required.`)
                          .nullable(),
                      }),
                  });
                }
              });

              return object().shape(shapes);
            }),
          })
          .nullable(),
      })
    ),
  });

  return (
    <Fragment>
      <ProjectAdminContainerStyled data-testid="weld-setup-container">
        <ProjectAdminHeaderStyled data-testid="weld-setup-header">
          <Text family="graphik" size="600" weight="600" color="white">
            <Trans>Features</Trans>
          </Text>
        </ProjectAdminHeaderStyled>
        {!featuresLoading && featuresError && (
          <Box
            flexDirection={{ sm: 'column' }}
            justifyContent={{ sm: 'center' }}
            alignItems={{ sm: 'center' }}
            flexGrow={{ sm: 1 }}
            data-testid="feature-error-retry"
          >
            <Box pb={{ sm: '500' }} flexDirection={{ sm: 'column' }} alignItems={{ sm: 'center' }}>
              <Text>
                <Trans>There was a problem loading project features.</Trans>
              </Text>
              <Text>
                <Trans>Please click the button below to try again.</Trans>
              </Text>
            </Box>
            <Box>
              <Button size={ButtonSize.MEDIUM} onClick={() => featuresRefetch()}>
                <Box mr={{ sm: '400' }}>
                  <Icon icon="undo" />
                </Box>
                <Trans>Retry</Trans>
              </Button>
            </Box>
          </Box>
        )}
        {featuresLoading && !featuresError && featuresList.length === 0 && (
          <Box
            flexGrow={{ sm: 1 }}
            alignItems={{ sm: 'center' }}
            flexDirection={{ sm: 'column' }}
            justifyContent={{ sm: 'center' }}
            data-testid="feature-loading"
          >
            <Spinner size={SpinnerSize.LARGE} />
          </Box>
        )}

        {!featuresLoading && !featuresError && featuresList.length === 0 && (
          <Box
            flexGrow={{ sm: 1 }}
            alignItems={{ sm: 'center' }}
            flexDirection={{ sm: 'column' }}
            justifyContent={{ sm: 'center' }}
            data-testid="feature-no-data"
          >
            <Trans>No feature found.</Trans>
          </Box>
        )}

        {featuresList.length > 0 && !featuresError && !featuresLoading && (
          <Formik
            enableReinitialize={true}
            initialValues={initialValues}
            validationSchema={FeatureValidationSchema}
            onSubmit={(values) => {
              const featuresItems = values.features.filter((featureItem) => {
                return initialValues.features.some((defaultItem) => {
                  return (
                    defaultItem.id === featureItem?.id &&
                    (defaultItem.enabled !== featureItem.enabled ||
                      (!isNull(defaultItem.metaData) && !isEqual(defaultItem.metaData, featureItem.metaData)))
                  );
                });
              });
              saveFeatures(featuresItems);
            }}
          >
            {(formikProps: FormikProps<FormikState>) => {
              const { dirty, isValid, handleSubmit, handleReset } = formikProps;
              return (
                <Fragment>
                  <ProjectAdminSubContainerStyled mt={{ sm: '450' }} data-testid="feature-data-container">
                    <Box my={{ sm: '550' }} mx={{ sm: '600' }} flexDirection={{ sm: 'column' }} flexGrow={{ sm: '1' }}>
                      <Heading family="graphik" size="500" weight="600">
                        <Trans>Activate or Deactivate Features</Trans>
                      </Heading>
                      <Box
                        pt={{ sm: '500' }}
                        pb={{ sm: '600' }}
                        css={`
                          border-bottom: 1px solid ${({ theme }) => theme.colors.grey[300]};
                        `}
                      >
                        <Text family="graphik" size="300" color="grey.1000">
                          <Trans>Choose which feature flags you want to use on your project.</Trans>
                        </Text>
                      </Box>
                      {map(featuresList, (feature, index) => {
                        return (
                          <ErrorBoundary>
                            <Suspense fallback={<SuspenseLoader></SuspenseLoader>}>
                              <FeatureItem key={feature.id} index={index} featureItem={feature} />
                            </Suspense>
                          </ErrorBoundary>
                        );
                      })}
                    </Box>
                  </ProjectAdminSubContainerStyled>
                  {!featuresLoading && !featuresError && (
                    <FooterStyled>
                      <Box>
                        <Box mr={{ sm: '400' }}>
                          <FooterButtonStyled
                            data-testid="feature-cancel"
                            disabled={saveFeatureLoading || !dirty}
                            onClick={() => {
                              handleReset();
                            }}
                            appearance={ButtonAppearance.SECONDARY}
                          >
                            <Trans>Discard Changes</Trans>
                          </FooterButtonStyled>
                        </Box>
                        <FooterButtonStyled
                          data-testid="feature-save"
                          onClick={() => {
                            handleSubmit();
                          }}
                          disabled={saveFeatureLoading || !dirty || !isValid}
                          appearance={ButtonAppearance.SECONDARY}
                        >
                          {saveFeatureLoading && <Spinner />}
                          {!saveFeatureLoading && t`Save Changes`}
                        </FooterButtonStyled>
                      </Box>
                    </FooterStyled>
                  )}
                </Fragment>
              );
            }}
          </Formik>
        )}
      </ProjectAdminContainerStyled>
      {saveFeatureError && (
        <ErrorBoundary>
          <Suspense fallback={<SuspenseLoader></SuspenseLoader>}>
            <SaveFeatureErrorModal
              saveFeatureError={saveFeatureError}
              close={() => {
                setSaveFeatureError(null);
              }}
            />
          </Suspense>
        </ErrorBoundary>
      )}
    </Fragment>
  );
};
